/* eslint-disable require-jsdoc */
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Grid,
  Tooltip,
  Typography
} from '@material-ui/core';
import createMuiTheme from '@material-ui/core/styles/createTheme';
import TextField from '@material-ui/core/TextField';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import locale from '../locale/Localization';
import {get} from '../script/notarized-objects-service';

const theme = createMuiTheme();

const useStyles = {
  autocomplete: {height: 30, width: '100%'},
  gridContainer: {
    margin: theme.spacing(1),
    paddingTop: '0px',
    paddingLeft: '0px'
  },
  gridItem: {padding: 0},
  searchField: {width: '102%'},
  searchHeadline: {margin: '15px 5px -15px'},
  accordionSummary: {height: '64px', margin: '0px', padding: '0px'},
  accordionDetails: {margin: '0px', paddingLeft: '0px'}
};

const DOCUMENT_NUMBER_OR_NAME = 'documentNumberOrName';
const NOTARIZED_OBJECT = 'notarizedObject';
const NOTARIZED_TYPE = 'notarizedType';
const CONTRACT = 'contract';
const YEAR = 'year';
const MONTH = 'month';
const SELECTED = 'Selected';

const typeOptions = [
  {
    group: NOTARIZED_TYPE,
    value: 'guarantee',
    label: locale.guarantee
  },
  {
    group: NOTARIZED_TYPE,
    value: 'marriageLaw',
    label: locale.marriageLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'lawOfSuccession',
    label: locale.lawOfSuccession
  },
  {
    group: NOTARIZED_TYPE,
    value: 'familyInheritanceLaw',
    label: locale.familyInheritanceLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'familyLaw',
    label: locale.familyLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'familyAndSoccessionLaw',
    label: locale.familyAndSoccessionLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'commercialLaw',
    label: locale.commercialLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'foundationLaw',
    label: locale.foundationLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'propertyLaw',
    label: locale.propertyLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'securitiesLaw',
    label: locale.securitiesLaw
  },
  {
    group: NOTARIZED_TYPE,
    value: 'authenticationOfCopy',
    label: locale.authenticationOfCopy
  },
  {
    group: NOTARIZED_TYPE,
    value: 'authenticationOfDate',
    label: locale.authenticationOfDate
  },
  {
    group: NOTARIZED_TYPE,
    value: 'authenticationOfSignature',
    label: locale.authenticationOfSignature
  },
  {
    group: NOTARIZED_TYPE,
    value: 'land',
    label: locale.land
  },
  {
    group: NOTARIZED_TYPE,
    value: 'land',
    label: locale.other
  }
];

const contractOptions = [
  {
    group: CONTRACT,
    value: 'contractAmongLiving',
    label: locale.contractAmongLiving
  },
  {
    group: CONTRACT,
    value: 'contractByDead',
    label: locale.contractByDead
  },
  {
    group: CONTRACT,
    value: 'contractByDead+contractAmongLiving',
    label: locale.contractAmongLiving + ' / ' + locale.contractByDead
  },
  {
    group: CONTRACT,
    value: 'authentication',
    label: locale.authentication
  }
];

const yearOptions = () => {
  moment.locale(locale.getLanguage());
  const year = moment().year();
  let currentYear = 2010;
  const options = [];
  while (currentYear <= year) {
    options.push({
      group: YEAR,
      value: currentYear,
      label: currentYear.toString()
    });
    currentYear++;
  }
  return options.reverse();
};

const monthOptions = () => {
  let currentMonth = 1;
  const options = [];
  while (currentMonth <= 12) {
    options.push({
      group: MONTH,
      rootgroup: YEAR,
      value: currentMonth,
      label: moment()
        .month(currentMonth - 1)
        .format('MMMM')
    });
    currentMonth++;
  }
  return options;
};

/**
 * Component class of ArchiveSearch.
 *
 */
export default class ArchiveSearch extends Component {
  // eslint-disable-next-line require-jsdoc
  constructor(props) {
    super(props);
    this.state = {
      timeout: null,
      notarizedObjectOptions: [],
      yearOptions: yearOptions(),
      monthOptions: monthOptions(),
      documentNumberOrName: '',
      notarizedObjectSelected: null,
      notarizedTypeSelected: null,
      contractSelected: null,
      yearSelected: null,
      monthSelected: null
    };
  }

  componentDidMount = () => {
    get().then((response) => {
      const notarizedObjectOptions = response.map((notarizedObject) => {
        return {
          group: NOTARIZED_OBJECT,
          value: notarizedObject.notarizedObject,
          label: notarizedObject.notarizedObject,
          type: notarizedObject.notarizedType,
          contract: notarizedObject.contract
        };
      });
      this.setState({notarizedObjectOptions: notarizedObjectOptions});
    });
  };

  /**
   * Handles the search input change.
   *
   * @param {Event} event - The change event.
   * @param {*} newValue - The value.
   * @param {Array} options - The current used options for the current field assignment.
   */
  handleSearchChange = (event: Event, newValue: any, options: Array) => {
    try {
      clearTimeout(this.state.timeout);
      this.handleSearchTerms(newValue, options);
      this.setState({
        timeout: setTimeout(() => {
          this.props.onSearchSubmit(this.getSearchTerms());
        }, 1000)
      });
    } catch (e) {
      console.error('Archive handle search error: ', e);
    }
  };

  /**
   * Handles the current search term and it's relation to other search terms.

   * @param {*} newValue - The value.
   * @param {Array} options - The current used options for the current field assignment.
   */
  async handleSearchTerms(newValue: any, options: Array) {
    try {
      let currentGroup = '';

      if (!options) {
        currentGroup = DOCUMENT_NUMBER_OR_NAME;
      } else {
        const currentField = options[0].group;
        currentGroup = currentField.concat(SELECTED);

        // Handle notarizedObject group selection
        if (currentField === NOTARIZED_OBJECT) {
          if (newValue) {
            const relatedNotarizedType = {
              group: NOTARIZED_TYPE,
              value: newValue.type,
              label: locale.getString(newValue.type)
            };
            const relatedContract = {
              group: CONTRACT,
              value: newValue.contract,
              label: locale.getString(newValue.contract)
            };
            this.setState({
              notarizedTypeSelected: relatedNotarizedType,
              contractSelected: relatedContract
            });
          }
        }

        // Handle notarizedType and contract degroup selection
        if (
          (currentField.includes(NOTARIZED_TYPE) ||
            currentField.includes(CONTRACT)) &&
          newValue
        ) {
          this.setState({notarizedObjectSelected: null});
        }

        // Handle en- & disabling of month search field depending on year selection
        if (currentField === YEAR && !newValue) {
          this.setState({monthSelected: null});
        }
      }
      await this.setState({[currentGroup]: newValue});
    } catch (error) {
      console.error('Handle search terms error: ', error);
    }
  }

  /**
   * Get all search terms.
   *
   * @return {Array} - The current search terms.
   */
  getSearchTerms() {
    try {
      const searchTerms = [];
      const searchTermNames = [
        DOCUMENT_NUMBER_OR_NAME,
        NOTARIZED_OBJECT.concat(SELECTED),
        NOTARIZED_TYPE.concat(SELECTED),
        CONTRACT.concat(SELECTED),
        YEAR.concat(SELECTED),
        MONTH.concat(SELECTED)
      ];

      for (const searchTermName of searchTermNames) {
        const currentSearchTerm = this.state[searchTermName];
        if (currentSearchTerm) {
          searchTerms.push(
            searchTermName === DOCUMENT_NUMBER_OR_NAME
              ? '*' + currentSearchTerm + '*'
              : currentSearchTerm
          );
        }
      }
      return searchTerms;
    } catch (error) {
      console.error('Get search terms error: ', error);
    }
  }

  /**
   * Resets the search fields.
   */
  resetSearch() {
    // Triggers initial archive view
    this.handleSearchChange(null, '', null);
    this.setState({
      documentNumberOrName: '',
      notarizedObjectSelected: null,
      notarizedTypeSelected: null,
      contractSelected: null,
      yearSelected: null,
      monthSelected: null
    });
  }

  /**
   * Sub-render method for the simple search box.
   *
   * @return {string} - The component contents.
   */
  renderSimpleSearchBox() {
    return (
      <Grid
        id='archive-search-row'
        container
        spacing={3}
        style={useStyles.gridContainer}
      >
        <Grid item xs={5} style={useStyles.gridItem}>
          <TextField
            aria-label='Archive Search'
            id='search-by-document-number-name'
            label={locale.archiveSearchLabel}
            placeholder={locale.searchByDocumentNumberOrName}
            type='search'
            style={useStyles.searchField}
            value={this.state.documentNumberOrName}
            onClick={(event) => event.stopPropagation()}
            onFocus={(event) => event.stopPropagation()}
            onChange={(event) =>
              this.handleSearchChange(event, event.target.value, null)
            }
          />
        </Grid>
        <Grid item xs={7} style={useStyles.gridItem}>
          <Typography
            variant='body1'
            style={useStyles.searchHeadline}
          ></Typography>
        </Grid>
      </Grid>
    );
  }

  /**
   * Sub-render method for advanced search fields.
   *
   * @param {string} id - The component id.
   * @param {Array} options - The selection options.
   * @param {string} value - The value from the state.
   * @param {Object} label - The text field component label.
   * @param {boolean} disabled - The boolean value to disable the component.
   * @param {string} placeholder - The text field component placeholder text.
   * @return {string} - The component contents.
   */
  renderAdvancedSearchField(
    id: String,
    options: Array,
    value: Object,
    label: String,
    disabled: Boolean,
    placeholder: String
  ) {
    return (
      <Autocomplete
        id={id}
        style={useStyles.autocomplete}
        disabled={disabled}
        options={options}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option) => option.value}
        autoComplete={false}
        autoHighlight={true}
        includeInputInList={false}
        clearOnEscape={true}
        value={value}
        onChange={(event, newValue) =>
          this.handleSearchChange(event, newValue, options)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            style={useStyles.searchField}
            margin='dense'
            label={label}
            placeholder={placeholder}
            InputLabelProps={{
              shrink: true
            }}
          />
        )}
      />
    );
  }

  /**
   * Sub-render method for the search box.
   *
   * @return {string} - The component contents.
   */
  // eslint-disable-next-line max-lines-per-function
  renderAdvancedSearchBox() {
    return (
      <Grid
        id='archive-search-row'
        container
        spacing={3}
        style={useStyles.gridContainer}
      >
        <Grid item xs={4} style={{paddingLeft: 0}}>
          {this.renderAdvancedSearchField(
            'search-by-notarized-object',
            this.state.notarizedObjectOptions,
            this.state.notarizedObjectSelected,
            locale.notarizedObject,
            false,
            ''
          )}
        </Grid>
        <Grid item xs={2}>
          {this.renderAdvancedSearchField(
            'search-by-notarized-type',
            typeOptions,
            this.state.notarizedTypeSelected,
            locale.notarizedType,
            false,
            ''
          )}
        </Grid>
        <Grid item xs={2}>
          {this.renderAdvancedSearchField(
            'search-by-contract',
            contractOptions,
            this.state.contractSelected,
            locale.contract,
            false,
            ''
          )}
        </Grid>
        <Grid item xs={2}>
          {this.renderAdvancedSearchField(
            'search-by-year',
            this.state.yearOptions,
            this.state.yearSelected,
            locale.year,
            false,
            ''
          )}
        </Grid>
        <Grid item xs={2}>
          {this.renderAdvancedSearchField(
            'search-by-month',
            this.state.monthOptions,
            this.state.monthSelected,
            locale.month,
            !this.state.yearSelected,
            ''
          )}
        </Grid>
      </Grid>
    );
  }

  /**
   * Renders the ArchiveSearch component.
   *
   * @return {string} - The archive search component to be rendered.
   */
  render() {
    return (
      <div>
        <Accordion elevation={0}>
          <AccordionSummary
            style={useStyles.accordionSummary}
            expandIcon={
              <Tooltip title={locale.advancedSearch} placement='left'>
                <ExpandMoreIcon color='primary' />
              </Tooltip>
            }
            aria-controls='archive-search-content'
            id='archive-search-box'
          >
            {this.renderSimpleSearchBox()}
          </AccordionSummary>
          <AccordionDetails style={useStyles.accordionDetails}>
            {this.renderAdvancedSearchBox()}
          </AccordionDetails>
          <Divider />
          <AccordionActions>
            <Tooltip title={locale.searchResetTooltip} placement='left'>
              <Button size='small' onClick={() => this.resetSearch()}>
                {locale.remove}
              </Button>
            </Tooltip>
          </AccordionActions>
        </Accordion>
      </div>
    );
  }
}

ArchiveSearch.propTypes = {
  onSearchSubmit: PropTypes.func.isRequired,
  useStyles: PropTypes.object
};
